/**
 * 首页活动模块与未登录活动弹层共用：活动筛选 + rewards → steps（与 TaskCenter 一致）
 */

export function isPublishedActivity(a) {
  const s = a?.status;
  return s === "PUBLISHED" || s === "published" || s === "Published";
}

/** 后端首页默认展示活动标记（兼容布尔 / 字符串 / 1） */
export function isDefaultInHomeActivity(a) {
  const v = a?.defaultInHome;
  return v === true || v === "true" || v === 1;
}

export function isHomeActivityCandidate(a) {
  return !!a && isPublishedActivity(a) && isDefaultInHomeActivity(a);
}

export function pickPublishedFromList(list) {
  if (!list || !Array.isArray(list) || list.length === 0) return null;
  return list.find((a) => isHomeActivityCandidate(a)) ?? null;
}

export function getJumpUrl(condition) {
  const urls = condition?.jumpUrls;
  if (!urls || typeof urls !== "object") return null;
  const path = urls.web != null && urls.web !== "" ? urls.web : urls.common;
  return path == null || path === "" ? null : path;
}

/** conditions[].depositConditionExt.amountRule：任务目标金额（如首充 ≥20 USDT） */
export function getDepositAmountRuleFromCondition(c) {
  const rule = c?.depositConditionExt?.amountRule;
  if (!rule || rule.amount == null || rule.amount === "") return null;
  const amt = typeof rule.amount === "number" ? rule.amount : parseFloat(String(rule.amount));
  if (Number.isNaN(amt) || amt <= 0) return null;
  const currency = (rule.currency != null && rule.currency !== "" ? String(rule.currency) : "USDT").toUpperCase();
  return { amount: amt, currency };
}

const CONDITION_NAME_I18N_KEYS = {
  register: "activity.conditionName.register",
  deposit: "more.deposit",
  trade: "activity.conditionName.trade",
  kyc: "activity.conditionName.kyc",
  ai_copytrade_follow: "activity.conditionName.aiCopytradeFollow",
  ai_copytrade_hold: "activity.conditionName.aiCopytradeHold",
};

export const AI_COPYTRADE_INVITE_ACTIVITY_CODES = [
  "PERMANENT_KYC_AI_UNFOLLOW",
  "PERMANENT_KYC_AI_UNFOLLOW_S2",
];

export const AI_COPYTRADE_INVITE_STRATEGY1_CODE = "PERMANENT_KYC_AI_UNFOLLOW";

export function isAiCopytradeInviteActivity(activity) {
  if (!activity || typeof activity !== "object") return false;
  return activity.inviteCategory === "AI_COPYTRADE";
}

export function filterAiCopytradeInviteActivities(list) {
  return (Array.isArray(list) ? list : []).filter(isAiCopytradeInviteActivity);
}

export function pickDefaultAiCopytradeInviteActivity(list) {
  const aiCopyActivities = filterAiCopytradeInviteActivities(list);
  return (
    aiCopyActivities.find((a) => a.activityCode === AI_COPYTRADE_INVITE_STRATEGY1_CODE) ??
    aiCopyActivities[0] ??
    null
  );
}

/** 条件 stepCode / handlerCode / conditionName 统一取值 */
export function resolveActivityConditionCode(condition) {
  const raw = condition?.stepCode ?? condition?.handlerCode ?? condition?.conditionName ?? "";
  return String(raw).trim();
}

export function isAiCopytradeHoldCondition(condition) {
  return resolveActivityConditionCode(condition).toUpperCase() === "AI_COPYTRADE_HOLD";
}

export function isAiCopytradeFollowCondition(condition) {
  return resolveActivityConditionCode(condition).toUpperCase() === "AI_COPYTRADE_FOLLOW";
}

/** 持满步骤无金额进度条；其余沿用原有 target/current 判断 */
export function shouldShowActivityConditionProgress(condition) {
  if (isAiCopytradeHoldCondition(condition)) return false;
  if (!condition) return false;
  const nTgt = Number(condition.progressTarget);
  const nCur = Number(condition.progressCurrent);
  const hasTarget =
    condition.progressTarget != null &&
    condition.progressTarget !== "" &&
    !Number.isNaN(nTgt) &&
    nTgt > 0;
  const hasCurrent =
    condition.progressCurrent != null &&
    condition.progressCurrent !== "" &&
    !Number.isNaN(nCur) &&
    nCur > 0;
  return hasTarget || hasCurrent;
}

/** 活动步骤/条件展示文案：优先描述，其次 stepCode 翻译 */
export function getActivityStepDisplayLabel(step, t) {
  if (!step) return "";
  const descRaw =
    step.stepDescription ??
    step.conditionDescription ??
    step.title ??
    step.description;
  if (descRaw != null && String(descRaw).trim() !== "") {
    return appendTradeMinLeverageToDescription(String(descRaw).trim(), step, t);
  }
  const code = resolveActivityConditionCode(step);
  const translated = translateActivityConditionName(code, t);
  if (translated) return translated;
  const fallback = step.stepName ?? step.name;
  return fallback != null && String(fallback).trim() !== "" ? String(fallback).trim() : "";
}

export function getAiCopytradeInviteCategoryLabel(t) {
  if (typeof t !== "function") return "AI Copy Trading Invite";
  const label = t("activity.inviteCategory.aiCopytrade");
  return label && label !== "activity.inviteCategory.aiCopytrade" ? label : "AI Copy Trading Invite";
}

function translateConditionNamePart(name, t) {
  const raw = String(name ?? "").trim();
  if (!raw) return "";
  if (typeof t !== "function") return raw;
  const key = CONDITION_NAME_I18N_KEYS[raw.toLowerCase()];
  if (!key) return raw;
  const translated = t(key);
  return translated && translated !== key ? translated : raw;
}

/** 接口 conditionName（如 Deposit、Trade）→ 多语言展示；支持 "A / B" 拼接 */
export function translateActivityConditionName(name, t) {
  const raw = String(name ?? "").trim();
  if (!raw) return "";
  if (!raw.includes("/")) return translateConditionNamePart(raw, t);
  return raw
    .split(/\s*\/\s*/)
    .map((part) => translateConditionNamePart(part, t))
    .filter(Boolean)
    .join(" / ");
}

/** rewards[].conditions[]：是否为交易类条件（conditionName 为 Trade，忽略大小写） */
export function isTradeActivityCondition(c) {
  if (!c) return false;
  const name = c.conditionName;
  if (name == null || String(name).trim() === "") return false;
  return String(name).trim().toLowerCase() === "trade";
}

/**
 * 在条件描述后追加最低杠杆说明；仅当 conditionName 为 Trade 且 params.minLeverage 有有效值时追加。
 * @param {string} baseText 已拼好的描述（如 conditionDescription）
 * @param {object} condition conditions[] 单项
 * @param {function} [t] i18n，可选
 */
export function appendTradeMinLeverageToDescription(baseText, condition, t) {
  const base = baseText != null && String(baseText).trim() !== "" ? String(baseText).trim() : "";
  if (!isTradeActivityCondition(condition)) return base;
  const raw = condition?.params?.minLeverage;
  if (raw === null || raw === undefined || raw === "") return base;
  if (typeof raw === "boolean") return base;
  const v = String(raw).trim();
  if (v === "") return base;
  const suffix = t
    ? t("activity.conditionMinLeverageSuffix", { leverage: v }) || ` (Minimum leverage: ${v})`
    : ` (Minimum leverage: ${v})`;
  return `${base}${suffix}`;
}

export function buildStepsFromRewards(rewards, t) {
  if (!rewards || !Array.isArray(rewards)) return [];
  const steps = [];
  const completedText = t ? t("taskCenter.completed") : "Completed";
  const pendingText = t ? t("taskCenter.pending") : "Pending";
  for (let rewardIndex = 0; rewardIndex < rewards.length; rewardIndex += 1) {
    const reward = rewards[rewardIndex];
    const conditions = reward.conditions || [];
    if (conditions.length === 0) continue;
    const sequential = reward.requireSequentialCompletion === true;
    if (sequential) {
      conditions.forEach((c) => {
        const descRaw = c.stepDescription ?? c.conditionDescription;
        steps.push({
          stepName: translateActivityConditionName(c.conditionName ?? c.stepCode ?? c.handlerCode, t),
          stepDescription: appendTradeMinLeverageToDescription(descRaw, c, t),
          progressText: c.progressText,
          progressCurrent: isAiCopytradeHoldCondition(c) ? undefined : c.progressCurrent,
          progressTarget: isAiCopytradeHoldCondition(c) ? undefined : c.progressTarget,
          progressCurrency: c.progressCurrency,
          buttonText: c.buttonText,
          completed: !!c.completed,
          jumpUrl: getJumpUrl(c),
          rewardIndex,
          depositAmountRule: getDepositAmountRuleFromCondition(c),
          stepCode: resolveActivityConditionCode(c),
        });
      });
    } else {
      const allCompleted = conditions.every((c) => !!c.completed);
      const names = conditions
        .map((c) => c.conditionName ?? c.stepCode ?? c.handlerCode)
        .filter(Boolean);
      const firstWithUrl = conditions.find((c) => getJumpUrl(c));
      const firstCondition = conditions[0];
      const depositFromCond =
        conditions.find((c) => getDepositAmountRuleFromCondition(c)) || firstCondition;
      steps.push({
        stepName:
          names.length > 0
            ? names.map((n) => translateActivityConditionName(n, t)).join(" / ")
            : reward.rewardDescription,
        stepDescription: reward.rewardDescription,
        progressText: allCompleted ? completedText : pendingText,
        progressCurrent: firstCondition?.progressCurrent,
        progressTarget: firstCondition?.progressTarget,
        progressCurrency: firstCondition?.progressCurrency,
        buttonText: firstCondition?.buttonText,
        completed: allCompleted,
        jumpUrl: firstWithUrl ? getJumpUrl(firstWithUrl) : null,
        rewardIndex,
        depositAmountRule: getDepositAmountRuleFromCondition(depositFromCond),
      });
    }
  }
  return steps;
}

export function getCompletedCount(steps) {
  if (!Array.isArray(steps) || steps.length === 0) return 0;
  return steps.filter((s) => s && s.completed).length;
}

export function getCurrentStepIndex(steps, completedCount) {
  if (!Array.isArray(steps) || steps.length === 0) return 1;
  return completedCount < steps.length ? completedCount + 1 : steps.length;
}

/** 充值跳转时附带活动目标金额，供 web-index 充值页默认填入 / 法币换算 */
export function appendActivityDepositQueryToPath(path, stepObj) {
  const rule = stepObj?.depositAmountRule;
  if (!path || typeof path !== "string" || !rule) return path;
  try {
    const qIndex = path.indexOf("?");
    const base = qIndex >= 0 ? path.slice(0, qIndex) : path;
    const existing = qIndex >= 0 ? path.slice(qIndex + 1) : "";
    const sp = new URLSearchParams(existing);
    sp.set("activityTargetAmount", String(rule.amount));
    sp.set("activityTargetCurrency", rule.currency);
    const q = sp.toString();
    return q ? `${base}?${q}` : base;
  } catch {
    return path;
  }
}
